import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity } from '@adornis/baseql/decorators/entity.js';
import { Field } from '@adornis/baseql/decorators/field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';

/**
 * Mirrors the task queue's own states. There is no `FINISHED`: a task that is over has left the queue, so
 * it is simply absent from the list — what became of it is in the `GenerationLog`.
 */
export enum GenerationTaskStatus {
  QUEUED = 'QUEUED',
  RUNNING = 'RUNNING',
  ERROR = 'ERROR',
  ABORTED = 'ABORTED',
}

registerEnum({
  name: 'GenerationTaskStatus',
  enum: GenerationTaskStatus,
});

/**
 * Live state of a queued generation, as read straight from the task queue.
 *
 * Ephemeral in the same sense the queue is: it exists while the job does. Nothing here is stored anywhere,
 * which is why it carries no result — a task that is over simply stops appearing, and what came of it is
 * looked up in the `GenerationLog`.
 */
@Entity()
export class GenerationTask extends AdornisEntity {
  static override _class = 'GenerationTask';

  @Field(() => ID)
  taskId: Maybe<string>;

  @Field(() => GenerationTaskStatus)
  status: Maybe<GenerationTaskStatus>;

  /**
   * The worker's latest progress message. Not persisted anywhere — a subscriber that attaches late simply
   * has none until the next update is published.
   */
  @Field(() => String)
  progress: Maybe<string>;

  /**
   * Where the current phase stands, when it is a loop with a known length — the extraction batches, the
   * templates being rendered. Both are set together or not at all; a phase that is a single step reports
   * only a message.
   */
  @Field(() => Int)
  progressCurrent: Maybe<number>;

  @Field(() => Int)
  progressTotal: Maybe<number>;

  /**
   * How often the task has been attempted. Above 1 means it was redelivered after a worker died.
   */
  @Field(() => Int)
  attempts: Maybe<number>;

  @Field(() => String)
  errorMessage: Maybe<string>;
}
